import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useUrlBuilder, ProductSwitcherLink } from '@app/router';
import { useCurrency } from '@app/currency';
import { getAlgoliaIndex, getAlgoliaQuery } from '@app/redux/browse/selectors';
import { trackEvent as segmentTrackEvent, EVENTS } from '@app/segment';
import { AnalyticsAttributes, Events, useGATrackEvent } from '@app/analytics';
import { Flex, useInView, Box, Text, Tag } from '@stockx/ui';
import FavoriteFromCell from '@app/favorite/components/FavoriteCell';
import { EXPERIMENTS } from '@app/config';
import { useExperiment } from '@app/experiments';
import { useIsNonInteractiveViewer } from '@app/ui';
import type { LegacyProduct } from '@app/types';
import { getSizeTypeLabel } from '@app/localizedSizes';
import { PLACEHOLDER_IMAGE_URL } from '../../consts';
import CatalogImageWrapper from '../CatalogImageWrapper';
import CatalogImage from '../CatalogImage';
import { TruncatedText } from './ProductTile.styles';

type ProductTileProps = {
  product: Partial<LegacyProduct>;
  index: number;
  tracking: string;
  swooshName?: string | null;
  screenName?: string | null;
  useMargin?: number;
  isPromotedProduct?: boolean;
  handleTrackEvent?: Function | null;
  children?: any;
  browsePageProps?: {
    urlParams: string;
    isSearch: boolean;
    isPriority?: boolean;
  };
};

const ProductTile = ({
  product = {},
  index,
  tracking,
  swooshName = null,
  screenName,
  isPromotedProduct,
  useMargin = 0,
  handleTrackEvent = null,
  children,
  browsePageProps,
}: ProductTileProps) => {
  const { title = 'Product', media: { smallImageUrl = null } = {} } = product;
  const buildUrl = useUrlBuilder();
  const isNonInteractiveViewer = useIsNonInteractiveViewer();
  const isSizeTypeLabelVariant =
    useExperiment(EXPERIMENTS.ab_size_type_labels_web) === 'variant';

  const isFavoriteTestEnabled =
    useExperiment(EXPERIMENTS.ab_pirate_product_cell_favorite_web_v1) ===
    'true';

  const { currency } = useCurrency();
  const { t } = useTranslation();
  const queryID = useSelector(getAlgoliaQuery);
  const algoliaIndex = useSelector(getAlgoliaIndex);
  const gaTrack = useGATrackEvent();
  const [ref, inView] = useInView({
    triggerOnce: true,
    initialInView: isNonInteractiveViewer,
  });

  const handleClick = useCallback(() => {
    if (!tracking) {
      return;
    }

    if (browsePageProps?.isSearch) {
      gaTrack(Events.SearchTileClick);
    }

    segmentTrackEvent(EVENTS.PRODUCT_TILE, {
      swooshName,
      screenName,
      product,
      productName: product?.title,
      productUUID: product?.id,
      vertical: product?.productCategory,
      position: index + 1,
      currency,
      queryID,
      index: algoliaIndex,
      objectID: product.uuid,
    });

    gaTrack(
      Events.productTileClick,
      product?.productCategory,
      product.uuid,
      null,
      {
        [AnalyticsAttributes.cdProductCollectionName]: tracking,
      },
    );
    gaTrack(tracking, product?.productCategory, product?.title);
  }, [
    currency,
    gaTrack,
    index,
    product,
    algoliaIndex,
    queryID,
    screenName,
    swooshName,
    tracking,
    browsePageProps?.isSearch,
  ]);

  return (
    <Box
      ref={ref}
      borderRadius="3px"
      minWidth="141px"
      height="100%"
      position="relative"
      sx={{ marginRight: useMargin }}
    >
      {isFavoriteTestEnabled && <FavoriteFromCell product={product} />}
      <ProductSwitcherLink
        to={buildUrl(`${product?.urlKey}${browsePageProps?.urlParams ?? ''}`)}
        onClick={handleTrackEvent || handleClick}
      >
        <Flex
          flexDirection="column"
          border="1px"
          borderColor="neutral.200"
          height="100%"
        >
          {isPromotedProduct && (
            <Tag width="72px" height="22px" marginLeft="10px" marginTop="5px">
              {t('product_tile_sponsored')}
            </Tag>
          )}
          <Box m={{ base: 2, lg: 4 }}>
            <CatalogImageWrapper width="140px" height="75px">
              <CatalogImage
                maxWidth="100%"
                objectFit="contain"
                src={
                  (inView || browsePageProps?.isPriority) && smallImageUrl
                    ? smallImageUrl
                    : PLACEHOLDER_IMAGE_URL
                }
                aria-hidden="true"
                alt={title}
                width={140}
                height={75}
                htmlHeight="75px"
                htmlWidth="140px"
                isPriority={browsePageProps?.isPriority}
              />
            </CatalogImageWrapper>
          </Box>
          <Flex
            height="100%"
            flexDirection="column"
            padding={2}
            textAlign="left"
            position="relative"
          >
            {isSizeTypeLabelVariant && (
              <Text fontSize="xs" lineHeight="xs" color="neutral.500" mb={1}>
                {t(
                  getSizeTypeLabel(
                    product?.browseVerticals?.[0],
                    product?.gender,
                  ),
                )}
              </Text>
            )}
            <Box>
              <TruncatedText
                color="neutral.black"
                fontSize={{ base: 'xs', md: 'sm' }}
                height={{ base: '34px', md: '40px' }}
                fontWeight="medium"
              >
                {product?.title}
              </TruncatedText>
            </Box>
            {children}
          </Flex>
        </Flex>
      </ProductSwitcherLink>
    </Box>
  );
};

export default ProductTile;
